import {
  DEFAULT_OG_IMAGE,
  LANDING_SEO,
  SITE_NAME,
  SITE_URL,
} from "@/constants/site-seo";

export type PageMeta = {
  title: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  noIndex?: boolean;
};

function upsertMetaTag(attribute: "name" | "property", key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertCanonicalLink(href: string) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  const selector = `script[data-jsonld-id="${id}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("script");
    element.setAttribute("type", "application/ld+json");
    element.setAttribute("data-jsonld-id", id);
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

export function removeJsonLd(id: string) {
  document.head.querySelector(`script[data-jsonld-id="${id}"]`)?.remove();
}

export function applyPageMeta(meta: PageMeta) {
  document.title = meta.title;

  const description = meta.description;
  const canonicalUrl = meta.canonicalPath
    ? `${SITE_URL}${meta.canonicalPath}`
    : `${SITE_URL}${window.location.pathname}`;
  const ogImage = meta.ogImage ?? DEFAULT_OG_IMAGE;

  if (description) {
    upsertMetaTag("name", "description", description);
    upsertMetaTag("property", "og:title", meta.title);
    upsertMetaTag("property", "og:description", description);
    upsertMetaTag("property", "og:site_name", SITE_NAME);
    upsertMetaTag("property", "og:url", canonicalUrl);
    upsertMetaTag("property", "og:image", ogImage);
    upsertMetaTag("name", "twitter:title", meta.title);
    upsertMetaTag("name", "twitter:description", description);
    upsertMetaTag("name", "twitter:image", ogImage);
    upsertMetaTag("name", "twitter:card", "summary_large_image");
  }

  if (meta.keywords) {
    upsertMetaTag("name", "keywords", meta.keywords);
  }

  upsertCanonicalLink(canonicalUrl);
  upsertMetaTag(
    "name",
    "robots",
    meta.noIndex ? "noindex, nofollow" : "index, follow",
  );
}

export function applyLandingJsonLd(faqs: Array<{ question: string; answer: string }>) {
  upsertJsonLd("landing-faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });

  upsertJsonLd("landing-organization", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description: LANDING_SEO.description,
  });
}

export function clearPageMeta(defaultTitle: string, defaultDescription: string) {
  applyPageMeta({
    title: defaultTitle,
    description: defaultDescription,
    keywords: LANDING_SEO.keywords,
    canonicalPath: "/",
  });
}
